import { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { periodRange } from "../data/revenuePeriods";
import {
  MAX_REVENUE_PAGES,
  fetchRevenuePage,
} from "../services/revenueService";
import type {
  RevenuePayment,
  RevenuePeriodId,
  RevenueTotals,
} from "../types/revenue.types";

export type RevenueStatus = "loading" | "ready" | "failed";

export interface RevenueState {
  /** Every payment raised inside the period, newest first. */
  payments: RevenuePayment[];
  /** The backend's own totals, over the same period as the rows. */
  totals: RevenueTotals;
  status: RevenueStatus;
  /** The server's reason for a failed load, where it gave one. */
  error?: string;
  /**
   * Whether the server refused the read outright rather than failing to answer
   * it. This endpoint answers 403 to a workspace with no plan running, which is
   * the ordinary state of one before its first purchase — so it is not an error
   * the reader can do anything about, and not news either: a practice that has
   * never been able to raise a payment has never raised one.
   *
   * The screen draws it as the empty ledger it is. See `RevenuePage`.
   */
  refused: boolean;
  /** What the failed state's retry calls. */
  reload: () => void;
  /**
   * Whether there are still pages coming in behind the ones handed over.
   *
   * The screen is drawn off the first page and the rest arrive under it, so
   * this is not a load anybody is waiting on — it is what lets the table say
   * "still coming" at the bottom of the rows instead of looking like the end
   * of the list.
   */
  loadingMore: boolean;
}

/** Handed out before the first load lands, and the same value every time: a
 *  fresh one per render would rebuild every memo reading it. */
const EMPTY: RevenuePayment[] = [];
const NO_TOTALS: RevenueTotals = { received: 0, pending: 0, cancelled: 0 };

export const revenueQueryKey = (period: RevenuePeriodId) =>
  ["revenue", period] as const;

/**
 * The workspace's payments over one period, loaded when the Revenue screen
 * opens and again whenever the period changes.
 *
 * The window is the server's: `period` becomes the endpoint's `from` and `to`,
 * so the rows and the totals that come back are both of the stretch of time the
 * screen is set to. "All time" sends neither and asks for everything, which is
 * what the screen opens on.
 *
 * Backed by TanStack Query, keyed by period: revisiting a period already
 * looked at this session shows its ledger immediately while a fresh read
 * checks it behind the scenes, so a payment confirmed on another device still
 * catches up within moments instead of making every switch wait on the
 * network.
 *
 * Read-only, because the screen is: a payment is settled from the record it was
 * raised against, so nothing here writes and the list only ever changes by
 * being loaded again.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useRevenue(period: RevenuePeriodId): RevenueState {
  const getToken = useTokenGetter();
  const toast = useToast();

  const query = useInfiniteQuery({
    queryKey: revenueQueryKey(period),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => {
      // Dated when the load starts rather than at render, so a screen left
      // open overnight asks for the window as it stands when it next asks at
      // all.
      const range = periodRange(period);
      return fetchRevenuePage(
        getToken,
        { from: range?.from, to: range?.to },
        pageParam,
        signal
      );
    },
    // The cap is the service's — a `next` that never turns null would
    // otherwise walk for as long as the screen is open.
    getNextPageParam: (last, pages) =>
      last.hasNext && pages.length < MAX_REVENUE_PAGES
        ? pages.length + 1
        : undefined,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  /**
   * The rest of the window, read in behind the first page.
   *
   * This is the whole point of paging the request: the screen is drawn from
   * the first fifty rows the moment they land, and the walk that used to keep
   * everybody waiting now happens under a table they are already reading.
   * Nothing here is awaited — each page that arrives simply adds its rows.
   */
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const pages = query.data?.pages;

  /**
   * Every row in hand, newest first.
   *
   * Sorted across the pages rather than trusting their order: each page is
   * ordered within itself, and a backend that answers page two with anything
   * out of step would otherwise show it that way.
   */
  const payments = useMemo(() => {
    if (!pages) return EMPTY;
    const rows = pages.flatMap((page) => page.payments);
    rows.sort(
      (a, b) =>
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
    );
    return rows;
  }, [pages]);

  /**
   * The three figures above the table.
   *
   * The backend's summary is of everything the query matched, not of the page
   * it came with — so the first page settles the tiles and every page after it
   * says the same thing. Without one, the pages' own totals are summed as they
   * land, which is why a tile can climb while the rows behind it are still
   * arriving.
   */
  const totals = useMemo(() => {
    if (!pages || pages.length === 0) return NO_TOTALS;
    if (pages[0].summarised) return pages[0].totals;

    return pages.reduce(
      (sums, page) => ({
        received: sums.received + page.totals.received,
        pending: sums.pending + page.totals.pending,
        cancelled: sums.cancelled + page.totals.cancelled,
      }),
      NO_TOTALS
    );
  }, [pages]);

  // 403 is this endpoint's answer to a workspace with no plan running — an
  // ordinary state, not a fault, and named separately from `isError` so the
  // screen can draw it as the empty ledger it is rather than a failure.
  const denied = query.error instanceof ApiError && query.error.status === 403;

  useEffect(() => {
    if (!query.isError) return;
    console.error("revenue failed to load:", query.error);
    // No toast for a refusal. A toast is for something that went wrong and
    // may not be visible where it happened; a workspace without a plan is
    // neither — the screen itself says what it is waiting for, and a red
    // card over it would report the ordinary state of every practice before
    // its first purchase as a fault.
    if (denied) return;
    toast.error(
      "Could not load your revenue",
      query.error instanceof ApiError
        ? query.error.message
        : "We could not reach the server. Check your connection and try again."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    payments,
    totals,
    // Only the first page is a load anybody waits on, and a page that fails
    // after rows are already on screen is not a screen that failed to load —
    // it is a list that stopped short. So `data` is what decides this, not the
    // query's status on its own.
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error" && !query.data
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    refused: query.isError && denied && !query.data,
    reload: () => {
      void query.refetch();
    },
    loadingMore: hasNextPage || isFetchingNextPage,
  };
}
